import { DependentsProvider } from '@cascade/react/dependents';
import { ExperienceListProvider } from '@cascade/react/experiences';
import { FeatureFlagGate } from '@cascade/react/projects';
import { defineRoutes } from '@cascade/ui-library';
import { Navigate, Outlet, redirect } from 'react-router-dom';
import type { LoaderFunctionArgs } from 'react-router-dom';
import { ErrorBoundary } from './components/error-boundary/index.ts';
import { InstallBanner } from './components/install-banner/index.ts';
import {
  CONVERSATION_PATH,
  DATA_CONTROLS_PATH,
  DEPENDENTS_PATH,
  EXPERIENCE_LINK_PATH,
  EXPERIENCE_PATH,
  EXPERIENCE_SUMMARY_PATH,
  getExperiencePath,
  getTicketPath,
  HOME_PATH,
  SETTINGS_PATH,
  TICKET_LINK_PATH,
  TICKET_PATH,
  TICKETS_PATH,
} from './config/index.ts';
import {
  DemoGate,
  DependentsGate,
  ExperienceGate,
  ProjectGate,
  SessionGate,
  TagsGate,
  TermsGate,
  TicketsGate,
} from './gates/index.ts';
import { Layout } from './layout/index.ts';
import {
  ConversationsProvider,
  ExperienceProvider,
  ProjectThemeProvider,
  TicketsProvider,
} from './providers/index.ts';
import { DataControls } from './views/data-controls/index.ts';
import { Dependents } from './views/dependents/index.ts';
import { Experience } from './views/experience/index.ts';
import { ExperienceSummary } from './views/experience-summary/index.ts';
import { Home } from './views/home/index.ts';
import { Settings } from './views/settings/index.ts';
import { Ticket } from './views/ticket/index.ts';
import { Tickets } from './views/tickets/index.ts';

const redirectToTicket = ({ params }: LoaderFunctionArgs) => {
  const ticketPath = getTicketPath(String(params.displayId));

  return redirect(ticketPath);
};

const redirectToExperience = ({ params, request }: LoaderFunctionArgs) => {
  const experienceId = String(params.experienceId);
  const experiencePath = getExperiencePath(experienceId);
  const subpath = params['*'] ? `/${params['*']}` : '';
  const { search } = new URL(request.url);

  return redirect(`${experiencePath}${subpath}${search}`);
};

export const { routes, routeObjects } = defineRoutes([
  {
    element: (
      <ErrorBoundary>
        <ProjectThemeProvider>
          <ConversationsProvider>
            <ExperienceListProvider>
              <Outlet />
              <FeatureFlagGate
                flag='enable_pwa_install'
                loading={null}
                disabled={null}
              >
                <InstallBanner />
              </FeatureFlagGate>
            </ExperienceListProvider>
          </ConversationsProvider>
        </ProjectThemeProvider>
      </ErrorBoundary>
    ),
    children: [
      { path: 'demo', name: 'demo', element: <DemoGate /> },
      {
        element: (
          <SessionGate>
            <ProjectGate>
              <TermsGate>
                <TagsGate>
                  <TicketsProvider>
                    <Layout mode='app'>
                      <ErrorBoundary>
                        <Outlet />
                      </ErrorBoundary>
                    </Layout>
                  </TicketsProvider>
                </TagsGate>
              </TermsGate>
            </ProjectGate>
          </SessionGate>
        ),
        children: [
          { index: true, name: 'home', element: <Home /> },
          { path: CONVERSATION_PATH, name: 'conversation', element: <Home /> },
          { path: SETTINGS_PATH, name: 'settings', element: <Settings /> },
          {
            path: DATA_CONTROLS_PATH,
            name: 'dataControls',
            element: <DataControls />,
          },
          {
            path: DEPENDENTS_PATH,
            name: 'dependents',
            element: (
              <DependentsGate>
                <DependentsProvider>
                  <Dependents />
                </DependentsProvider>
              </DependentsGate>
            ),
          },
          {
            path: TICKETS_PATH,
            name: 'tickets',
            element: (
              <TicketsGate>
                <Tickets />
              </TicketsGate>
            ),
          },
          {
            path: TICKET_PATH,
            name: 'ticket',
            element: (
              <TicketsGate>
                <Ticket />
              </TicketsGate>
            ),
          },
          { path: TICKET_LINK_PATH, loader: redirectToTicket },
          {
            path: EXPERIENCE_PATH,
            element: (
              <FeatureFlagGate
                flag='enable_ge'
                loading={null}
                disabled={<Navigate to={HOME_PATH} replace />}
              >
                <ExperienceProvider>
                  <ExperienceGate>
                    <Outlet />
                  </ExperienceGate>
                </ExperienceProvider>
              </FeatureFlagGate>
            ),
            children: [
              { index: true, name: 'experience', element: <Experience /> },
              {
                path: EXPERIENCE_SUMMARY_PATH,
                name: 'experienceSummary',
                element: <ExperienceSummary />,
              },
            ],
          },
          { path: EXPERIENCE_LINK_PATH, loader: redirectToExperience },
          { path: '*', element: <Navigate to={HOME_PATH} replace /> },
        ],
      },
    ],
  },
]);
